import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  api, ApiFailure, VISIT_TYPE_LABEL, PAYMENT_MODE_LABEL, PATIENT_CATEGORIES, categoryText,
  type DashboardReport, type DiagnosisRow, type NamedFrequencyRow,
  type OutstandingRow, type TimingRow,
  type CashReconciliation,
  type PatientCategory,
} from '../api'

function today(): string {
  return new Date().toLocaleDateString('en-CA')
}

function daysAgo(n: number): string {
  const d = new Date()
  d.setDate(d.getDate() - n)
  return d.toLocaleDateString('en-CA')
}

function money(v: string | number | null | undefined): string {
  if (v == null) return '—'
  const n = Number(v)
  return Number.isFinite(n) ? `₹${n.toLocaleString('en-IN')}` : '—'
}

/** A compact horizontal bar for a frequency row, sized against the biggest. */
function Bar({ value, max }: { value: number; max: number }) {
  const w = max > 0 ? Math.max(4, Math.round((value / max) * 100)) : 0
  return <div className="freq-bar"><span style={{ width: `${w}%` }} /></div>
}

interface Range { from: string; to: string }

export default function Reports() {
  const navigate = useNavigate()

  const [range, setRange] = useState<Range>({ from: today(), to: today() })
  /** Which patient line the reports cover; empty means both. */
  const [category, setCategory] = useState<PatientCategory | ''>('')
  const [dash, setDash] = useState<DashboardReport | null>(null)
  const [diagnoses, setDiagnoses] = useState<DiagnosisRow[]>([])
  const [tests, setTests] = useState<NamedFrequencyRow[]>([])
  const [medicines, setMedicines] = useState<NamedFrequencyRow[]>([])
  const [outstanding, setOutstanding] = useState<{ rows: OutstandingRow[]; total: string }>({ rows: [], total: '0' })
  const [timings, setTimings] = useState<TimingRow[]>([])
  const [msg, setMsg] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const load = useCallback(async () => {
    setBusy(true); setMsg(null)
    try {
      const qs = `from=${range.from}&to=${range.to}${category ? `&category=${category}` : ''}`
      const [d, dx, tx, mx, out, tm] = await Promise.all([
        api.get<DashboardReport>(`/api/reports/dashboard?${qs}`),
        api.get<{ diagnoses: DiagnosisRow[] }>(`/api/reports/diagnoses?${qs}`),
        api.get<{ tests: NamedFrequencyRow[] }>(`/api/reports/tests?${qs}`),
        api.get<{ medicines: NamedFrequencyRow[] }>(`/api/reports/medicines?${qs}`),
        api.get<{ outstanding: OutstandingRow[]; total: string }>(`/api/reports/outstanding?${qs}`),
        api.get<{ timings: TimingRow[] }>(`/api/reports/timings?${qs}`),
      ])
      setDash(d); setDiagnoses(dx.diagnoses); setTests(tx.tests); setMedicines(mx.medicines)
      setOutstanding({ rows: out.outstanding, total: out.total }); setTimings(tm.timings)
    } catch (err) {
      setMsg(err instanceof ApiFailure ? err.message : 'Could not load the reports.')
    } finally {
      setBusy(false)
    }
  }, [range, category])

  useEffect(() => { void load() }, [load])

  function setPreset(preset: 'today' | 'week' | 'month') {
    if (preset === 'today') setRange({ from: today(), to: today() })
    else if (preset === 'week') setRange({ from: daysAgo(6), to: today() })
    else setRange({ from: daysAgo(29), to: today() })
  }

  const maxDoctor = useMemo(
    () => dash?.byDoctor.reduce((m, r) => Math.max(m, Number(r.revenue) || 0), 0) ?? 0,
    [dash],
  )
  const maxDx = diagnoses[0]?.count ?? 0
  const maxTest = tests[0]?.count ?? 0
  const maxMed = medicines[0]?.count ?? 0

  /**
   * The new / returning split over *every* visit in the range.
   *
   * This used to divide by new + follow_up alone, which silently dropped
   * review and emergency visits: a day of 2 new and 5 review read as
   * "100% new". Review and emergency are returning patients for this purpose,
   * so they belong in the denominator, and the percentages now sum to 100.
   */
  const newVsReturning = useMemo(() => {
    if (!dash) return { newN: 0, returningN: 0, ratio: '' }
    const count = (t: string) => dash.byType.find((r) => r.visitType === t)?.count ?? 0
    const newN = count('new')
    const returningN = count('follow_up') + count('review') + count('emergency')
    const total = newN + returningN
    if (total === 0) return { newN, returningN, ratio: '' }
    const newPct = Math.round((newN / total) * 100)
    return {
      newN,
      returningN,
      ratio: `${newPct}% new · ${100 - newPct}% returning`,
    }
  }, [dash])

  return (
    <>
      <div className="page-head">
        <h2>Reports</h2>
        <p>Everything the day-end tally, the audit and the monthly review need.</p>
      </div>

      {msg && <div className="msg error">{msg}</div>}

      <div className="panel report-range">
        <div className="records-views">
          <button className="chip" onClick={() => setPreset('today')}>Today</button>
          <button className="chip" onClick={() => setPreset('week')}>Last 7 days</button>
          <button className="chip" onClick={() => setPreset('month')}>Last 30 days</button>
          <span className="spacer" />
          <input type="date" value={range.from} onChange={(e) => setRange((r) => ({ ...r, from: e.target.value }))} />
          <span>to</span>
          <input type="date" value={range.to} onChange={(e) => setRange((r) => ({ ...r, to: e.target.value }))} />
          {busy && <span className="panel-note" style={{ margin: 0 }}>Loading…</span>}
        </div>
        <div className="records-views" style={{ marginTop: 10 }} role="group" aria-label="Patient category">
          <button className={category === '' ? 'chip is-on' : 'chip'} onClick={() => setCategory('')}>All patients</button>
          {PATIENT_CATEGORIES.map((c) => (
            <button key={c} className={category === c ? 'chip is-on' : 'chip'} onClick={() => setCategory(c)}>
              {categoryText(c)}
            </button>
          ))}
        </div>
      </div>

      {dash && (
        <div className="stat-strip">
          {/* The new/returning split describes visits, not money, so it sits
              under the visit count rather than under the takings. */}
          <StatCard
            label="Visits"
            value={String(dash.totals.visits)}
            sub={[`${dash.totals.completed} completed`, newVsReturning.ratio].filter(Boolean).join(' · ')}
          />
          <StatCard label="Collected" value={money(dash.totals.collected)} sub={`over ${dash.totals.visits} visits`} />
          <StatCard label="Outstanding" value={money(dash.totals.outstanding)} sub={`${outstanding.rows.length} visits owing`} />
        </div>
      )}

      <div className="panel">
        <h3>By patient category</h3>
        <table className="records-table">
          <thead>
            <tr><th>Category</th><th className="num">Patients</th><th className="num">Visits</th><th className="num">Revenue</th></tr>
          </thead>
          <tbody>
            {PATIENT_CATEGORIES.map((c) => {
              const r = dash?.byCategory.find((x) => x.category === c)
              return (
                <tr key={c}>
                  <td>{categoryText(c)}</td>
                  <td className="num">{r?.patients ?? 0}</td>
                  <td className="num">{r?.visits ?? 0}</td>
                  <td className="num">{money(r?.revenue ?? 0)}</td>
                </tr>
              )
            })}
            {dash?.byCategory.find((x) => x.category === null) && (
              <tr>
                <td>{categoryText(null)} <span className="panel-note">(registered before categories)</span></td>
                <td className="num">{dash.byCategory.find((x) => x.category === null)!.patients}</td>
                <td className="num">{dash.byCategory.find((x) => x.category === null)!.visits}</td>
                <td className="num">{money(dash.byCategory.find((x) => x.category === null)!.revenue)}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="panel">
        <h3>By doctor</h3>
        {dash?.byDoctor.length === 0 && <p className="panel-note">No visits in this range.</p>}
        <table className="records-table">
          <thead>
            <tr><th>Doctor</th><th className="num">Visits</th><th className="num">Revenue</th><th></th></tr>
          </thead>
          <tbody>
            {dash?.byDoctor.map((r) => (
              <tr key={r.doctorId}>
                <td>{r.doctorName}</td>
                <td className="num">{r.count}</td>
                <td className="num">{money(r.revenue)}</td>
                <td className="freq-cell"><Bar value={Number(r.revenue) || 0} max={maxDoctor} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="report-grid">
        <div className="panel">
          <h3>Most frequent diagnoses</h3>
          {diagnoses.length === 0 && <p className="panel-note">No signed diagnoses yet.</p>}
          <table className="records-table">
            <tbody>
              {diagnoses.map((r, i) => (
                <tr key={i}>
                  <td>{r.diagnosis}</td>
                  <td className="num">{r.count}</td>
                  <td className="freq-cell"><Bar value={r.count} max={maxDx} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="panel">
          <h3>Most ordered tests</h3>
          {tests.length === 0 && <p className="panel-note">No tests advised in this range.</p>}
          <table className="records-table">
            <tbody>
              {tests.map((r, i) => (
                <tr key={i}>
                  <td>{r.name}</td>
                  <td className="num">{r.count}</td>
                  <td className="freq-cell"><Bar value={r.count} max={maxTest} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="panel">
          <h3>Most prescribed medicines</h3>
          {medicines.length === 0 && <p className="panel-note">No prescriptions in this range.</p>}
          <table className="records-table">
            <tbody>
              {medicines.map((r, i) => (
                <tr key={i}>
                  <td>{r.name}</td>
                  <td className="num">{r.count}</td>
                  <td className="freq-cell"><Bar value={r.count} max={maxMed} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="panel">
          <h3>Payment mix</h3>
          <table className="records-table">
            <tbody>
              {dash?.byMode.map((r) => (
                <tr key={r.mode}>
                  <td>{PAYMENT_MODE_LABEL[r.mode]}</td>
                  <td className="num">{r.count}</td>
                  <td className="num">{money(r.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <h3 style={{ marginTop: 18 }}>Visit type</h3>
          <table className="records-table">
            <tbody>
              {dash?.byType.map((r) => (
                <tr key={r.visitType}>
                  <td>{VISIT_TYPE_LABEL[r.visitType]}</td>
                  <td className="num">{r.count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="panel">
        <h3>Wait and consultation timings</h3>
        <p className="panel-note">
          Averages over completed visits. Wait = arrived to in-consult; consult = in-consult to signed.
        </p>
        <table className="records-table">
          <thead>
            <tr>
              <th>Doctor</th>
              <th className="num">Consults</th>
              <th className="num">Avg wait (min)</th>
              <th className="num">Avg consult (min)</th>
            </tr>
          </thead>
          <tbody>
            {timings.length === 0 && (
              <tr><td colSpan={4}><div className="empty">No timings yet — the report needs visits that reached in-consult and signed.</div></td></tr>
            )}
            {timings.map((r) => (
              <tr key={r.doctorId}>
                <td>{r.doctorName}</td>
                <td className="num">{r.consults}</td>
                <td className="num">{r.avgWaitMin}</td>
                <td className="num">{r.avgConsultMin}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="panel">
        <h3>Outstanding dues <span className="panel-note" style={{ margin: 0 }}>· total {money(outstanding.total)}</span></h3>
        <div className="table-wrap">
          <table className="records-table">
            <thead>
              <tr>
                <th>Date</th><th>Patient</th><th>Doctor</th>
                <th className="num">Payable</th><th className="num">Paid</th><th className="num">Balance</th><th></th>
              </tr>
            </thead>
            <tbody>
              {outstanding.rows.length === 0 && (
                <tr><td colSpan={7}><div className="empty">Nothing owing.</div></td></tr>
              )}
              {outstanding.rows.map((r) => (
                <tr key={r.visitId}>
                  <td className="nowrap">{r.visitDate.slice(5)}</td>
                  <td>
                    <div className="rec-name">{r.patientName}</div>
                    <div className="rec-code">{[r.patientCode, r.mobile].filter(Boolean).join(' · ')}</div>
                    <div className="rec-code">{categoryText(r.patientCategory)}</div>
                  </td>
                  <td className="nowrap">{r.doctorName.replace(/^Dr\.?\s*/, 'Dr ')}</td>
                  <td className="num">{money(r.netPayable)}</td>
                  <td className="num">{money(r.amountPaid)}</td>
                  <td className="num"><strong>{money(r.balance)}</strong></td>
                  <td><button className="link" onClick={() => navigate(`/visits/${r.visitId}`)}>Open</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <CashDrawer />
    </>
  )
}

/**
 * Day-end drawer count. What the books say should be in the drawer - cash
 * taken, less cash handed back - against what was actually counted, so a
 * discrepancy is found the same evening rather than a month later. Independent
 * of the report range above: a drawer is counted for one day, not a period.
 */
function CashDrawer() {
  const [date, setDate] = useState(today())
  const [counted, setCounted] = useState('')
  const [data, setData] = useState<CashReconciliation | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const load = useCallback(async (withCount: string) => {
    setBusy(true); setError(null)
    try {
      const qs = new URLSearchParams({ date })
      if (withCount.trim() !== '') qs.set('counted', withCount.trim())
      setData(await api.get<CashReconciliation>(`/api/reports/cash-reconciliation?${qs}`))
    } catch (err) {
      setError(err instanceof ApiFailure ? err.message : 'Could not read the drawer.')
    } finally {
      setBusy(false)
    }
  }, [date])

  // The expected figure is worth showing before anything has been counted, so
  // it loads on arrival and on a date change, and again once a count is typed.
  useEffect(() => { void load('') }, [load])

  const diff = data?.discrepancy == null ? null : Number(data.discrepancy)

  return (
    <div className="panel">
      <h3>Cash drawer</h3>
      <p className="panel-note" style={{ marginTop: 0 }}>
        Cash only. Card, UPI and net banking settle to the bank and never touch the drawer.
      </p>

      {error && <div className="msg error">{error}</div>}

      <div className="drawer-row">
        <div className="field">
          <label htmlFor="drawer-date">Day</label>
          <input id="drawer-date" type="date" value={date} max={today()}
            onChange={(e) => { setDate(e.target.value); setCounted('') }} />
        </div>
        <div className="field">
          <label htmlFor="drawer-counted">Counted in the drawer</label>
          <input id="drawer-counted" type="number" min="0" step="0.01" value={counted}
            placeholder="Count the notes and enter the total"
            onChange={(e) => setCounted(e.target.value)} />
        </div>
        <button disabled={busy || counted.trim() === ''} onClick={() => void load(counted)}>
          Check
        </button>
      </div>

      <div className="stat-strip" style={{ marginTop: 14 }}>
        <StatCard label="Cash taken" value={money(data?.cashCollected)} />
        <StatCard label="Cash refunded" value={money(data?.cashRefunded)} />
        <StatCard label="Should be in the drawer" value={money(data?.expected)} />
      </div>

      {diff != null && (
        <div className={`msg ${diff === 0 ? 'ok' : 'warn'}`}>
          {diff === 0
            ? `Counted ${money(data!.counted)} — the drawer balances.`
            : diff > 0
              ? `Counted ${money(data!.counted)} — ${money(diff)} more than the books. `
                + 'Look for a receipt that was never written.'
              : `Counted ${money(data!.counted)} — ${money(Math.abs(diff))} short. `
                + 'Look for change given without a receipt, or a payment recorded but not collected.'}
        </div>
      )}
    </div>
  )
}

function StatCard({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="stat-card">
      <div className="stat-label">{label}</div>
      <div className="stat-value">{value}</div>
      {sub && <div className="stat-sub">{sub}</div>}
    </div>
  )
}
